'use client';

import React, { useState, useEffect, useRef } from 'react';
import { analyzeOrderFlow } from './MarketAnalysisStatic'; 

const colors = {
  bg: '#020617', cardBg: '#0f172a', border: '#1e293b',
  textMain: '#f8fafc', textSub: '#94a3b8',
  green: '#22c55e', red: '#ef4444', warn: '#eab308',
  redBg: 'rgba(239, 68, 68, 0.1)', greenBg: 'rgba(34, 197, 94, 0.1)'
};

const defaultSettings = {
  enabled: false, 
  triggerRule1: true, 
  triggerWhale: true,
  triggerReversal: true, 
  reversalPct: 30, // [MỚI]: Ngưỡng % 30m > 1h (Default: 30%)
  triggerPricePct: false,
  pricePctTargets: [],    
  triggerPriceTarget: false,
  priceTargets: [],       
  triggerMA: true,
  maInterval: '1h',
  thresholdPct: 0.2, 
  cooldownMin: 15,
  heartbeatMin: 60
};

const fetchMAData = async (symbol, interval) => {
  try {
    const res = await fetch(`https://fapi.binance.com/fapi/v1/klines?symbol=${symbol}&interval=${interval}&limit=50`);
    const klines = await res.json();
    const closes = klines.map(k => parseFloat(k[4]));

    const sma7 = closes.slice(-7).reduce((a, b) => a + b, 0) / 7;
    const sma25 = closes.slice(-25).reduce((a, b) => a + b, 0) / 25;

    const prevSma7 = closes.slice(-8, -1).reduce((a, b) => a + b, 0) / 7;
    const prevSma25 = closes.slice(-26, -1).reduce((a, b) => a + b, 0) / 25;

    let crossStatus = "none";
    if (prevSma7 <= prevSma25 && sma7 > sma25) crossStatus = "golden";
    else if (prevSma7 >= prevSma25 && sma7 < sma25) crossStatus = "death";

    return { sma7, sma25, crossStatus };
  } catch (e) {
    console.error("Lỗi fetch MA:", e);
    return null;
  }
};

export default function TelegramAlert({ symbol, liveDataRef, smartMoneyRef }) {
  const [isOpen, setIsOpen] = useState(false);
  
  const [settings, setSettings] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('telegramAlertSettings');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (parsed.priceTargetValue > 0 && (!parsed.priceTargets || parsed.priceTargets.length === 0)) {
          parsed.priceTargets = [{ id: Date.now(), condition: 'above', price: parsed.priceTargetValue }];
        }
        if (parsed.pricePctValue > 0 && (!parsed.pricePctTargets || parsed.pricePctTargets.length === 0)) {
          parsed.pricePctTargets = [
            { id: Date.now() + 1, condition: 'up', value: parsed.pricePctValue },
            { id: Date.now() + 2, condition: 'down', value: parsed.pricePctValue }
          ];
        }
        if (parsed.triggerReversal === undefined) parsed.triggerReversal = true;
        if (parsed.reversalPct === undefined) parsed.reversalPct = 30; // Nạp mặc định 30% nếu chưa có
        
        return { ...defaultSettings, ...parsed };
      }
    }
    return defaultSettings;
  });

  const [tempSettings, setTempSettings] = useState(settings);
  const [status, setStatus] = useState("Đang chờ dữ liệu...");

  const baselineRef = useRef(null);
  const symbolChangeTimeRef = useRef(Date.now()); 
  
  const lastAlertTimeRef = useRef(0);
  const lastSignalTypeRef = useRef(""); 
  const lastHeartbeatRef = useRef(Date.now());
  const lastAlertedWhaleVolRef = useRef(null); 
  const lastAlertedPriceRef = useRef(null); 
  const prevPriceRef = useRef(null);        
  const lastMACrossRef = useRef("none"); 

  const lastReversalTypeRef = useRef("none");
  const lastReversalTimeRef = useRef(0);

  const triggeredTargetsRef = useRef(new Set());

  useEffect(() => {
    baselineRef.current = null;
    lastAlertedWhaleVolRef.current = null;
    lastAlertedPriceRef.current = null;
    prevPriceRef.current = null;
    lastAlertTimeRef.current = 0;
    lastSignalTypeRef.current = "";
    lastHeartbeatRef.current = Date.now();
    lastMACrossRef.current = "none"; 
    
    lastReversalTypeRef.current = "none";
    lastReversalTimeRef.current = 0;
    
    symbolChangeTimeRef.current = Date.now(); 
    triggeredTargetsRef.current.clear(); 
    setStatus(`Đã reset bộ nhớ cho mã ${symbol}`);
  }, [symbol]);

  useEffect(() => {
    triggeredTargetsRef.current.clear();
  }, [settings.priceTargets]);

  const fNum = (val) => {
    if (val === undefined || val === null) return '0';
    if (val >= 1000) return val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    if (val >= 1) return val.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 4 });
    if (val >= 0.001) return val.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 6 });
    return val.toLocaleString('en-US', { minimumFractionDigits: 8, maximumFractionDigits: 8 });
  };

  const fUSD = (val) => {
    if (!val) return '$0';
    if (Math.abs(val) >= 1e9) return `$${(Math.abs(val) / 1e9).toFixed(2)}B`;
    if (Math.abs(val) >= 1e6) return `$${(Math.abs(val) / 1e6).toFixed(2)}M`;
    if (Math.abs(val) >= 1e3) return `$${(Math.abs(val) / 1e3).toFixed(2)}K`;
    return `$${val.toFixed(2)}`;
  };

  const fQty = (val) => {
    if (!val) return '0';
    return val.toLocaleString('en-US', { maximumFractionDigits: 2 });
  };

  const handleOpenModal = () => {
    setTempSettings(settings);
    setIsOpen(true);
  };

  const handleSaveSettings = () => {
    const cleanTargets = tempSettings.priceTargets
      .map(t => ({ ...t, price: parseFloat(t.price) || 0 }))
      .filter(t => t.price > 0);

    const cleanPctTargets = tempSettings.pricePctTargets
      .map(t => ({ ...t, value: parseFloat(t.value) || 0 }))
      .filter(t => t.value > 0);

    const finalSettings = {
      ...tempSettings,
      priceTargets: cleanTargets,
      pricePctTargets: cleanPctTargets,
      reversalPct: parseFloat(tempSettings.reversalPct) || 0, // Lưu giá trị Reversal %
      thresholdPct: parseFloat(tempSettings.thresholdPct) || 0,
      cooldownMin: parseInt(tempSettings.cooldownMin) || 0,
      heartbeatMin: parseInt(tempSettings.heartbeatMin) || 0
    };

    setSettings(finalSettings);
    if (typeof window !== 'undefined') {
      localStorage.setItem('telegramAlertSettings', JSON.stringify(finalSettings));
    }
    setIsOpen(false);
  };

  const sendTelegramMsg = async (messageText) => {
    try {
      setStatus("Đang gửi Telegram...");
      const botToken = process.env.NEXT_PUBLIC_BOT_TOKEN;
      const chatId = process.env.NEXT_PUBLIC_CHAT_ID;

      if (!botToken || !chatId) {
        setStatus("Lỗi: Chưa cấu hình Token .env");
        return;
      }

      const telegramUrl = `https://api.telegram.org/bot${botToken}/sendMessage`;
      const res = await fetch(telegramUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chat_id: chatId, text: messageText, parse_mode: 'HTML', disable_web_page_preview: true })
      });

      const data = await res.json();
      if (data.ok) setStatus(`Đã gửi lúc ${new Date().toLocaleTimeString()}`);
      else setStatus(`Gửi lỗi! ${data.description}`);
    } catch (e) {
      setStatus("Lỗi kết nối mạng (API Telegram).");
    }
  };

  const buildMessage = (type, analysis, live, sm, caseString, base, maData, maInterval) => {
    const isLong = analysis.signalType === 'bullish';
    const sideName = analysis.signalType === 'bearish' ? 'SHORT' : 'LONG';
    
    const longWhaleAvg = sm.longWhalesAvgEntryPrice;
    const shortWhaleAvg = sm.shortWhalesAvgEntryPrice;
    const longWhaleQty = sm.longWhalesQty || 0;
    const shortWhaleQty = sm.shortWhalesQty || 0;
    const longWhaleUsd = longWhaleQty * (live.last || 0);
    const shortWhaleUsd = shortWhaleQty * (live.last || 0);

    let longPnLPct = 0; let shortPnLPct = 0;
    if (live.last) {
      if (longWhaleAvg) longPnLPct = ((live.last - longWhaleAvg) / longWhaleAvg) * 100;
      if (shortWhaleAvg) shortPnLPct = ((shortWhaleAvg - live.last) / shortWhaleAvg) * 100; 
    }
    
    const longPnLStr = longPnLPct >= 0 ? `+${longPnLPct.toFixed(2)}% 🟢` : `${longPnLPct.toFixed(2)}% 🔴`;
    const shortPnLStr = shortPnLPct >= 0 ? `+${shortPnLPct.toFixed(2)}% 🟢` : `${shortPnLPct.toFixed(2)}% 🔴`;

    const accRatio = sm.shortTraders > 0 ? (sm.longTraders / sm.shortTraders).toFixed(2) : 'N/A';
    const volRatio = sm.shortTradersQty > 0 ? (sm.longTradersQty / sm.shortTradersQty).toFixed(2) : 'N/A';
    const frStr = live.fundingRate !== undefined ? `${live.fundingRate > 0 ? '+' : ''}${(live.fundingRate * 100).toFixed(4)}%` : 'N/A';

    const targetWinRate = sideName === 'LONG' ? (sm.longProfitTraders / sm.longTraders * 100) : (sm.shortProfitTraders / sm.shortTraders * 100);
    const targetTraders = sideName === 'LONG' ? sm.longTraders : sm.shortTraders;
    const targetWhaleAcc = sideName === 'LONG' ? sm.longWhales : sm.shortWhales;
    const targetRetailAcc = targetTraders - targetWhaleAcc;
    const whaleVolPct = (sideName === 'LONG' ? sm.longTradersQty : sm.shortTradersQty) > 0 ? ((sideName === 'LONG' ? sm.longWhalesQty : sm.shortWhalesQty) / (sideName === 'LONG' ? sm.longTradersQty : sm.shortTradersQty)) * 100 : 0;

    const pDiff = ((live.last - base.last) / base.last) * 100;
    const vDiff = ((live.vol - base.vol) / base.vol) * 100;
    const oDiff = ((live.oiContract - base.oiContract) / base.oiContract) * 100;
    const baseTimeStr = base.timestamp ? new Date(base.timestamp).toLocaleTimeString('vi-VN') : "Lúc bật Bot";

    let msg = `🚨 <b>[${type === 'heartbeat' ? 'BÁO CÁO ĐỊNH KỲ' : 'TÍN HIỆU ĐẸP'}] - ${symbol}</b>\n`;
    
    msg += `📍 <b>Mã Case:</b> <code>${caseString}</code>\n`;
    msg += `🎯 <b>Gợi ý:</b> ${isLong ? 'LONG 🟢' : (analysis.signalType==='bearish' ? 'SHORT 🔴' : 'QUAN SÁT ⚪')} | <b>Uy tín:</b> ${analysis.winRate}%\n`;
    msg += `📝 <b>Logic:</b> ${analysis.comment}\n\n`;

    if (maData) {
      msg += `📈 <b>CHỈ BÁO MA (Khung ${maInterval})</b>\n`;
      msg += `• MA7: <code>$${fNum(maData.sma7)}</code> | MA25: <code>$${fNum(maData.sma25)}</code>\n`;
      if (maData.crossStatus === 'golden') {
        msg += `✨ <b>Tín hiệu:</b> MA7 vừa <b>CẮT LÊN</b> MA25 (Golden Cross) 🟢\n\n`;
      } else if (maData.crossStatus === 'death') {
        msg += `☠️ <b>Tín hiệu:</b> MA7 vừa <b>CẮT XUỐNG</b> MA25 (Death Cross) 🔴\n\n`;
      } else {
        const trendStr = maData.sma7 > maData.sma25 ? "Tăng (MA7 nằm trên MA25) 🟢" : "Giảm (MA7 nằm dưới MA25) 🔴";
        msg += `• Trạng thái Trend: <b>${trendStr}</b>\n\n`;
      }
    }

    msg += `⏱️ <b>CHI TIẾT BIẾN ĐỘNG (So với: ${baseTimeStr})</b>\n`;
    msg += `• Giá: <code>$${fNum(base.last)}</code> ➡️ <code>$${fNum(live.last)}</code> (<b>${pDiff > 0 ? '+' : ''}${pDiff.toFixed(2)}%</b>)\n`;
    msg += `• Vol: <code>${fUSD(base.vol)}</code> ➡️ <code>${fUSD(live.vol)}</code> (<b>${vDiff > 0 ? '+' : ''}${vDiff.toFixed(2)}%</b>)\n`;
    msg += `• O.I: <code>${fQty(base.oiContract)}</code> ➡️ <code>${fQty(live.oiContract)}</code> (<b>${oDiff > 0 ? '+' : ''}${oDiff.toFixed(2)}%</b>)\n`;
    msg += `• CVD 24H: <b>${live.cvd > 0 ? '+' : ''}${fUSD(live.cvd)}</b> ${live.cvd > 0 ? '🟢' : '🔴'}\n\n`;

    msg += `💰 <b>SOI VỊ THẾ CÁ MẬP (WHALES)</b>\n`;
    msg += `🟢 <b>Whale Long:</b> <b>${fQty(longWhaleQty)}</b> coin (${fUSD(longWhaleUsd)})\n`;
    msg += `  ↳ Entry: <code>$${fNum(longWhaleAvg)}</code> | PnL: <b>${longPnLStr}</b>\n`;
    msg += `🔴 <b>Whale Short:</b> <b>${fQty(shortWhaleQty)}</b> coin (${fUSD(shortWhaleUsd)})\n`;
    msg += `  ↳ Entry: <code>$${fNum(shortWhaleAvg)}</code> | PnL: <b>${shortPnLStr}</b>\n\n`;

    msg += `📊 <b>TỔNG QUAN DÒNG TIỀN</b>\n`;
    msg += `• OI Quote: <b>${fUSD(live.oiContract * live.last)}</b>\n`;
    msg += `• L/S Vol Ratio: <b>${volRatio}</b> | L/S Acc: <b>${accRatio}</b>\n`;
    msg += `• Funding Rate: <b>${frStr}</b>\n\n`;

    msg += `🟢 <b>MỔ XẺ PHE LONG (BÒ)</b>\n`;
    msg += `• Lợi nhuận (WR): <b>${targetWinRate.toFixed(1)}%</b>\n`;
    msg += `• Tổng Trader: <b>${fQty(sm.longTraders)}</b> tk\n`;
    msg += `  ├── Cá Mập: <b>${fQty(sm.longWhales)}</b> (${whaleVolPct.toFixed(1)}% Vol)\n`;
    msg += `  └── Nhỏ Lẻ: <b>${fQty(targetRetailAcc)}</b>\n`;

    return msg;
  };

  const buildMAMessage = (maData, interval) => {
    let msg = `⚔️ <b>[TEST CHỈ BÁO MA] - ${symbol}</b>\n`;
    msg += `⏱ Khung thời gian: <b>${interval}</b>\n\n`;
    msg += `• MA7 : <code>$${fNum(maData.sma7)}</code>\n`;
    msg += `• MA25: <code>$${fNum(maData.sma25)}</code>\n\n`;

    if (maData.crossStatus === 'golden') {
      msg += `✨ <b>Tín hiệu:</b> MA7 vừa <b>CẮT LÊN</b> MA25 (Golden Cross) 🟢\n`;
    } else if (maData.crossStatus === 'death') {
      msg += `☠️ <b>Tín hiệu:</b> MA7 vừa <b>CẮT XUỐNG</b> MA25 (Death Cross) 🔴\n`;
    } else {
      const trendStr = maData.sma7 > maData.sma25 ? "Tăng (MA7 nằm trên MA25) 🟢" : "Giảm (MA7 nằm dưới MA25) 🔴";
      msg += `• Trạng thái Trend: <b>${trendStr}</b>\n`;
    }
    return msg;
  };

  useEffect(() => {
    if (!settings.enabled) return;

    const interval = setInterval(async () => {
      const live = liveDataRef?.current;
      const sm = smartMoneyRef?.current;
      if (!live || !sm || !live.vol || !live.oiContract) return;

      if (live.symbol && live.symbol !== symbol) return;

      if (!baselineRef.current) {
        if (Date.now() - symbolChangeTimeRef.current < 5000) return;

        baselineRef.current = { ...live, timestamp: Date.now() };
        lastAlertedWhaleVolRef.current = (sm.longWhalesQty + sm.shortWhalesQty) / (sm.longTradersQty + sm.shortTradersQty) * 100;
        lastAlertedPriceRef.current = live.last; 
        prevPriceRef.current = live.last;
        return;
      }

      const base = baselineRef.current;
      const now = Date.now();
      const isHeartbeatTime = settings.heartbeatMin > 0 && ((now - lastHeartbeatRef.current) > settings.heartbeatMin * 60 * 1000);

      // ==============================================
      // 1. MODULE: CHECK QUAY XE 30M > 1H (CÓ BỘ LỌC %)
      // ==============================================
      if (settings.triggerReversal) {
        try {
          const [res30m, res1h] = await Promise.all([
            fetch(`https://www.binance.com/bapi/futures/v1/public/future/smart-money/signal/details/stats?symbol=${symbol}&timeRange=30m`),
            fetch(`https://www.binance.com/bapi/futures/v1/public/future/smart-money/signal/details/stats?symbol=${symbol}&timeRange=1h`)
          ]);
          
          const json30m = await res30m.json();
          const json1h = await res1h.json();

          if (json30m.success && json1h.success && json30m.data && json1h.data) {
            const wl30 = json30m.data.longWhalePositions || 0;
            const wl1 = json1h.data.longWhalePositions || 0;
            const ws30 = json30m.data.shortWhalePositions || 0;
            const ws1 = json1h.data.shortWhalePositions || 0;

            const longDiff = wl30 - wl1;
            const shortDiff = ws30 - ws1;

            // Tính tỷ lệ % Vượt mặt
            const targetPct = parseFloat(settings.reversalPct) || 0;
            let longPctJump = wl1 > 0 ? (longDiff / wl1) * 100 : (wl30 > 0 ? 999 : 0); // Tránh chia cho 0
            let shortPctJump = ws1 > 0 ? (shortDiff / ws1) * 100 : (ws30 > 0 ? 999 : 0);

            let revType = 'none';
            let jumpMsg = '';

            // Bắt kịch bản bên nào chênh lệch rõ ràng hơn & Vượt quá ngưỡng % cài đặt
            if (longDiff > 0 && wl30 > 0 && longDiff > shortDiff && longPctJump >= targetPct) {
              revType = 'long_reversal'; 
              jumpMsg = `Vượt +${longPctJump > 900 ? '∞' : longPctJump.toFixed(1)}%`;
            } else if (shortDiff > 0 && ws30 > 0 && shortDiff > longDiff && shortPctJump >= targetPct) {
              revType = 'short_reversal';
              jumpMsg = `Vượt +${shortPctJump > 900 ? '∞' : shortPctJump.toFixed(1)}%`;
            }

            if (revType !== 'none') {
              const isRevCooldownOver = (now - lastReversalTimeRef.current) > settings.cooldownMin * 60 * 1000;
              
              if (revType !== lastReversalTypeRef.current || isRevCooldownOver) {
                const isLong = revType === 'long_reversal';
                const title = isLong ? "🚀 [QUAY XE BẮT ĐÁY - LONG REVERSAL]" : "🩸 [QUAY XE ÚP SỌT - SHORT REVERSAL]";
                const icon = isLong ? "🟢" : "🔴";
                const action = isLong ? "LONG" : "SHORT";

                let revMsg = `${title} - <b>${symbol}</b>\n\n`;
                if (isLong) {
                  revMsg += `Cá mập Long vừa vớt đáy toàn bộ lực bán trước đó!\n`;
                  revMsg += `• Net Whale Long 30m: <b>${fUSD(wl30)}</b> (<b>${jumpMsg}</b> so với 1h)\n`;
                  revMsg += `• Net Whale Long 1h: <b>${fUSD(wl1)}</b>\n`;
                  revMsg += `<i>(Khối lượng Long 30m > 1h chứng tỏ 30 phút đầu phe Long bị xả, nhưng 30 phút sau Cá Mập đã gom ngược lại toàn bộ!)</i>\n\n`;
                } else {
                  revMsg += `Cá mập Short vừa xả lượng hàng khủng khiếp đè nát phe Long!\n`;
                  revMsg += `• Net Whale Short 30m: <b>${fUSD(ws30)}</b> (<b>${jumpMsg}</b> so với 1h)\n`;
                  revMsg += `• Net Whale Short 1h: <b>${fUSD(ws1)}</b>\n`;
                  revMsg += `<i>(Khối lượng Short 30m > 1h chứng tỏ 30 phút đầu phe Short bị ép lui, nhưng 30 phút sau Cá Mập quay xe nã đạn Short ròng!)</i>\n\n`;
                }
                revMsg += `🎯 <b>Hành động:</b> Cân nhắc tìm điểm <b>${action}</b> ${icon}\n`;
                revMsg += `• Giá Live: <code>$${fNum(live.last)}</code>\n`;

                sendTelegramMsg(revMsg);
                lastReversalTypeRef.current = revType;
                lastReversalTimeRef.current = now;
              }
            }
          }
        } catch(e) {
          console.error("Lỗi fetch reversal:", e);
        }
      }

      // ==============================================
      // 2. CÁC MODULE CẢNH BÁO KHÁC (GIỮ NGUYÊN BẢN GỐC)
      // ==============================================
      let maData = null;
      if (settings.triggerMA || isHeartbeatTime) {
        maData = await fetchMAData(symbol, settings.maInterval);
      }

      const pDiff = (live.last - base.last) / base.last * 100;
      const oDiff = (live.oiContract - base.oiContract) / base.oiContract * 100;
      const vDiff = (live.vol - base.vol) / base.vol * 100;
      
      const p = pDiff > settings.thresholdPct ? 'up' : (pDiff < -settings.thresholdPct ? 'down' : 'neutral');
      const o = oDiff > settings.thresholdPct ? 'up' : (oDiff < -settings.thresholdPct ? 'down' : 'neutral');
      const v = vDiff > settings.thresholdPct ? 'up' : (vDiff < -settings.thresholdPct ? 'down' : 'neutral');
      const c = live.cvd > 0 ? 'up' : 'down'; 

      const analysis = analyzeOrderFlow(p, v, o, c);
      const caseString = `${p}_${v}_${o}_${c}`; 
      
      if (settings.triggerMA && maData) {
        if (maData.crossStatus !== 'none' && maData.crossStatus !== lastMACrossRef.current) {
          const isGolden = maData.crossStatus === "golden";
          const msg = `⚔️ <b>[GIAO CẮT MA ${settings.maInterval}] - ${symbol}</b>\n\n` +
                      `${isGolden ? '🟢 <b>GOLDEN CROSS (MA7 CẮT LÊN MA25)</b>' : '🔴 <b>DEATH CROSS (MA7 CẮT XUỐNG MA25)</b>'}\n` +
                      `• MA7 : <code>$${fNum(maData.sma7)}</code>\n` +
                      `• MA25: <code>$${fNum(maData.sma25)}</code>\n` +
                      `<i>(Xu hướng trên khung ${settings.maInterval} đang đảo chiều!)</i>`;
          sendTelegramMsg(msg);
          lastMACrossRef.current = maData.crossStatus; 
        }
      }

      if (settings.triggerRule1) {
        const isGoodSetup = analysis.winRate >= 70 || analysis.signalType === 'warning';
        const isCooldownOver = (now - lastAlertTimeRef.current) > settings.cooldownMin * 60 * 1000;
        const isDifferentSignal = analysis.signalType !== lastSignalTypeRef.current;

        if (isGoodSetup && (isCooldownOver || isDifferentSignal)) {
          const msg = buildMessage('alert', analysis, live, sm, caseString, base, maData, settings.maInterval); 
          sendTelegramMsg(msg);
          lastAlertTimeRef.current = now;
          lastSignalTypeRef.current = analysis.signalType;
          baselineRef.current = { ...live, timestamp: now }; 
        }
      }

      if (settings.triggerWhale && lastAlertedWhaleVolRef.current) {
        const currentWhaleVol = (sm.longWhalesQty + sm.shortWhalesQty) / (sm.longTradersQty + sm.shortTradersQty) * 100;
        const deltaWhale = currentWhaleVol - lastAlertedWhaleVolRef.current;

        if (Math.abs(deltaWhale) >= 2.0) {
          const msg = `🐳 <b>[CẢNH BÁO CÁ MẬP DỒN LỰC] - ${symbol}</b>\n\n` +
                      `Tỷ trọng Volume Cá Mập vừa <b>${deltaWhale > 0 ? 'TĂNG' : 'GIẢM'} ${Math.abs(deltaWhale).toFixed(2)}%</b>.\n` +
                      `• Tỷ trọng hiện tại: <b>${currentWhaleVol.toFixed(2)}%</b>\n` +
                      `• Giá Live: <code>$${fNum(live.last)}</code>\n`;
          sendTelegramMsg(msg);
          lastAlertedWhaleVolRef.current = currentWhaleVol;
        }
      }

      if (settings.triggerPricePct && lastAlertedPriceRef.current && settings.pricePctTargets && settings.pricePctTargets.length > 0) {
        const deltaPricePct = ((live.last - lastAlertedPriceRef.current) / lastAlertedPriceRef.current) * 100;
        let triggeredPctTarget = null;

        for (const target of settings.pricePctTargets) {
          if (target.condition === 'up' && deltaPricePct >= target.value) {
            triggeredPctTarget = target;
            break; 
          } else if (target.condition === 'down' && deltaPricePct <= -target.value) { 
            triggeredPctTarget = target;
            break;
          }
        }

        if (triggeredPctTarget) {
          const isUp = triggeredPctTarget.condition === 'up';
          const msg = `📈 <b>[CẢNH BÁO BIẾN ĐỘNG GIÁ] - ${symbol}</b>\n\n` +
                      `Giá vừa <b>${isUp ? 'BƠM LÊN' : 'SẬP XUỐNG'} ${Math.abs(deltaPricePct).toFixed(2)}%</b> so với mốc gần nhất.\n` +
                      `• Mốc cũ: <code>$${fNum(lastAlertedPriceRef.current)}</code>\n` +
                      `• Giá Live: <code>$${fNum(live.last)}</code>\n`;
          sendTelegramMsg(msg);
          lastAlertedPriceRef.current = live.last; 
        }
      }

      if (settings.triggerPriceTarget && settings.priceTargets && settings.priceTargets.length > 0) {
        if (prevPriceRef.current) {
          const curr = live.last;
          const prev = prevPriceRef.current;
          
          settings.priceTargets.forEach(target => {
            if (triggeredTargetsRef.current.has(target.id)) return;

            let isTriggered = false;
            if (target.condition === 'above' && prev < target.price && curr >= target.price) {
              isTriggered = true;
            } else if (target.condition === 'below' && prev > target.price && curr <= target.price) {
              isTriggered = true;
            }

            if (isTriggered) {
              const directionText = target.condition === 'above' ? 'VƯỢT LÊN' : 'PHÁ XUỐNG';
              const icon = target.condition === 'above' ? '🟢' : '🔴';
              
              const msg = `🎯 <b>[CHẠM MỐC GIÁ] - ${symbol}</b>\n\n` +
                          `Giá đã <b>${directionText}</b> mốc bạn cài đặt: <b>$${fNum(target.price)}</b> ${icon}\n` +
                          `• Giá Live: <code>$${fNum(curr)}</code>\n`;
              sendTelegramMsg(msg);
              triggeredTargetsRef.current.add(target.id); 
            }
          });
        }
      }

      prevPriceRef.current = live.last;

      if (isHeartbeatTime) {
         const msg = buildMessage('heartbeat', analysis, live, sm, caseString, base, maData, settings.maInterval); 
         sendTelegramMsg(msg);
         lastHeartbeatRef.current = now;
      }

    }, 15000); 

    return () => clearInterval(interval);
  }, [settings, symbol]); 

  const handleManualTestFull = async () => {
    const live = liveDataRef?.current;
    const sm = smartMoneyRef?.current;
    
    if (!live || !sm || (live.symbol && live.symbol !== symbol)) {
      alert("⚠️ Dữ liệu mạng đang chuyển đổi sang coin mới, vui lòng chờ 2-3 giây rồi bấm lại!"); 
      return;
    }
    
    setStatus("Đang fetch dữ liệu MA...");
    const maData = await fetchMAData(symbol, tempSettings.maInterval);
    const analysis = analyzeOrderFlow('up', 'neutral', 'up', 'down'); 
    const caseString = 'up_neutral_up_down'; 
    const base = baselineRef.current || { ...live, timestamp: Date.now() };

    const msg = buildMessage('heartbeat', analysis, live, sm, caseString, base, maData, tempSettings.maInterval);
    sendTelegramMsg(msg);
  };

  const handleManualTestMA = async () => {
    setStatus("Đang fetch dữ liệu MA...");
    const maData = await fetchMAData(symbol, tempSettings.maInterval);
    if (!maData) {
      setStatus("Lỗi khi lấy dữ liệu MA!"); return;
    }
    const msg = buildMAMessage(maData, tempSettings.maInterval);
    sendTelegramMsg(msg);
  };

  const inputStyle = { width: '80px', padding: '4px 8px', backgroundColor: colors.bg, color: 'white', border: `1px solid ${colors.border}`, borderRadius: '4px' };

  return (
    <>
      <button 
        onClick={handleOpenModal}
        style={{ backgroundColor: colors.cardBg, border: `1px solid ${settings.enabled ? colors.green : colors.border}`, color: colors.textMain, padding: '8px 16px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px' }}
      >
        🔔 {settings.enabled ? <span style={{color: colors.green}}>Alert ON</span> : <span style={{color: colors.textSub}}>Alert OFF</span>}
      </button>

      {isOpen && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 9999 }}>
          <div style={{ backgroundColor: colors.cardBg, border: `1px solid ${colors.border}`, borderRadius: '12px', padding: '24px', width: '95%', maxWidth: '450px', color: colors.textMain, maxHeight: '90vh', overflowY: 'auto' }}>
            
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h3 style={{ margin: 0 }}>⚙️ Cấu hình Telegram Alert</h3>
              <button onClick={() => setIsOpen(false)} style={{ background: 'none', border: 'none', color: colors.textSub, cursor: 'pointer', fontSize: '18px' }}>✖</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              
              <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer', backgroundColor: 'rgba(255,255,255,0.05)', padding: '12px', borderRadius: '8px' }}>
                <input type="checkbox" checked={tempSettings.enabled} onChange={e => setTempSettings({...tempSettings, enabled: e.target.checked})} style={{ width: '18px', height: '18px' }}/>
                <strong style={{ color: tempSettings.enabled ? colors.green : colors.textSub }}>BẬT / TẮT TOÀN BỘ BOT</strong>
              </label>

              <div style={{ padding: '12px', border: `1px solid ${colors.border}`, borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: colors.textSub }}>📉 CHỈ BÁO MA (MOVING AVERAGE)</div>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input type="checkbox" checked={tempSettings.triggerMA} onChange={e => setTempSettings({...tempSettings, triggerMA: e.target.checked})} />
                  <span style={{ fontSize: '14px' }}>Báo động Độc lập khi MA7 Cắt MA25</span>
                </label>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: colors.textSub }}>Khung thời gian soi MA:</span>
                  <select 
                    value={tempSettings.maInterval} 
                    onChange={e => setTempSettings({...tempSettings, maInterval: e.target.value})} 
                    style={{...inputStyle, width: '100px', cursor: 'pointer'}}
                  >
                    <option value="1m">1 phút</option>
                    <option value="5m">5 phút</option>
                    <option value="15m">15 phút</option>
                    <option value="1h">1 giờ</option>
                    <option value="4h">4 giờ</option>
                  </select>
                </div>
              </div>

              {/* [CẬP NHẬT]: THÊM NGƯỠNG % CHO TÍNH NĂNG QUAY XE */}
              <div style={{ padding: '12px', border: `1px solid ${colors.border}`, borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: colors.textSub }}>🧠 LOGIC & CÁ MẬP</div>
                
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input type="checkbox" checked={tempSettings.triggerReversal} onChange={e => setTempSettings({...tempSettings, triggerReversal: e.target.checked})} />
                  <span style={{ fontSize: '14px', color: tempSettings.triggerReversal ? colors.green : colors.textMain }}>Báo động Quay Xe 🚀🩸 (30m &gt; 1h)</span>
                </label>
                
                {tempSettings.triggerReversal && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', paddingLeft: '24px', paddingBottom: '8px', borderBottom: `1px dashed ${colors.border}` }}>
                    <span style={{ fontSize: '13px', color: colors.textSub }}>Độ lệch (30m &gt; 1h) tối thiểu (%):</span>
                    <input 
                      type="number" 
                      value={tempSettings.reversalPct} 
                      onChange={e => setTempSettings({...tempSettings, reversalPct: e.target.value})} 
                      style={inputStyle} 
                    />
                  </div>
                )}

                <label style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input type="checkbox" checked={tempSettings.triggerRule1} onChange={e => setTempSettings({...tempSettings, triggerRule1: e.target.checked})} />
                  <span style={{ fontSize: '14px' }}>Báo động Setup Tốt (Win Rate ≥ 70%) & Bẫy giá</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <input type="checkbox" checked={tempSettings.triggerWhale} onChange={e => setTempSettings({...tempSettings, triggerWhale: e.target.checked})} />
                  <span style={{ fontSize: '14px' }}>Báo động Cá Mập (Biến động Vol ≥ 2%)</span>
                </label>
              </div>

              <div style={{ padding: '12px', border: `1px solid ${colors.border}`, borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ fontWeight: 'bold', fontSize: '13px', color: colors.textSub }}>💰 PRICE ACTION (CẢNH BÁO GIÁ)</div>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', borderBottom: `1px dashed ${colors.border}`, paddingBottom: '12px', marginBottom: '4px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" checked={tempSettings.triggerPricePct} onChange={e => setTempSettings({...tempSettings, triggerPricePct: e.target.checked})} />
                    <span style={{ fontSize: '14px' }}>Cảnh báo Biến động Giá (%):</span>
                  </label>
                  
                  {tempSettings.triggerPricePct && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '24px' }}>
                      {tempSettings.pricePctTargets?.map((target, idx) => (
                        <div key={target.id} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <select 
                            value={target.condition} 
                            onChange={e => {
                              const newTargets = [...tempSettings.pricePctTargets];
                              newTargets[idx].condition = e.target.value;
                              setTempSettings({...tempSettings, pricePctTargets: newTargets});
                            }}
                            style={{ ...inputStyle, width: '100px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            <option value="up">&gt;= (Tăng %)</option>
                            <option value="down">&lt;= (Giảm %)</option>
                          </select>
                          
                          <input 
                            type="number" 
                            step="0.1"
                            value={target.value} 
                            onChange={e => {
                              const newTargets = [...tempSettings.pricePctTargets];
                              newTargets[idx].value = e.target.value;
                              setTempSettings({...tempSettings, pricePctTargets: newTargets});
                            }} 
                            style={{ ...inputStyle, flex: 1 }} 
                            placeholder="Nhập %..."
                          />
                          
                          <button 
                            onClick={() => {
                              const newTargets = tempSettings.pricePctTargets.filter((_, i) => i !== idx);
                              setTempSettings({...tempSettings, pricePctTargets: newTargets});
                            }} 
                            style={{ background: colors.redBg, color: colors.red, border: 'none', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer' }}
                            title="Xóa mốc này"
                          >✖</button>
                        </div>
                      ))}
                      
                      <button 
                        onClick={() => {
                          const newTargets = tempSettings.pricePctTargets || [];
                          setTempSettings({...tempSettings, pricePctTargets: [...newTargets, { id: Date.now(), condition: 'up', value: '' }]});
                        }} 
                        style={{ background: 'transparent', border: `1px dashed ${colors.border}`, color: colors.textSub, padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                      >
                        + THÊM MỐC BIẾN ĐỘNG (%)
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input type="checkbox" checked={tempSettings.triggerPriceTarget} onChange={e => setTempSettings({...tempSettings, triggerPriceTarget: e.target.checked})} />
                    <span style={{ fontSize: '14px' }}>Cảnh báo khi chạm mốc giá cụ thể ($):</span>
                  </label>
                  
                  {tempSettings.triggerPriceTarget && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', paddingLeft: '24px' }}>
                      {tempSettings.priceTargets?.map((target, idx) => (
                        <div key={target.id} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <select 
                            value={target.condition} 
                            onChange={e => {
                              const newTargets = [...tempSettings.priceTargets];
                              newTargets[idx].condition = e.target.value;
                              setTempSettings({...tempSettings, priceTargets: newTargets});
                            }}
                            style={{ ...inputStyle, width: '100px', cursor: 'pointer', fontSize: '12px' }}
                          >
                            <option value="above">&gt;= (Vượt lên)</option>
                            <option value="below">&lt;= (Phá xuống)</option>
                          </select>
                          
                          <input 
                            type="number" 
                            step="0.00001"
                            value={target.price} 
                            onChange={e => {
                              const newTargets = [...tempSettings.priceTargets];
                              newTargets[idx].price = e.target.value;
                              setTempSettings({...tempSettings, priceTargets: newTargets});
                            }} 
                            style={{ ...inputStyle, flex: 1 }} 
                            placeholder="Nhập giá..."
                          />
                          
                          <button 
                            onClick={() => {
                              const newTargets = tempSettings.priceTargets.filter((_, i) => i !== idx);
                              setTempSettings({...tempSettings, priceTargets: newTargets});
                            }} 
                            style={{ background: colors.redBg, color: colors.red, border: 'none', borderRadius: '4px', padding: '6px 10px', cursor: 'pointer' }}
                            title="Xóa mốc này"
                          >✖</button>
                        </div>
                      ))}
                      
                      <button 
                        onClick={() => {
                          const newTargets = tempSettings.priceTargets || [];
                          setTempSettings({...tempSettings, priceTargets: [...newTargets, { id: Date.now(), condition: 'above', price: '' }]});
                        }} 
                        style={{ background: 'transparent', border: `1px dashed ${colors.border}`, color: colors.textSub, padding: '8px', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: 'bold' }}
                      >
                        + THÊM MỐC GIÁ
                      </button>
                    </div>
                  )}
                </div>

              </div>

              <div style={{ padding: '12px', border: `1px solid ${colors.border}`, borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: colors.textSub }}>Ngưỡng lọc nhiễu (%):</span>
                  <input type="number" step="0.1" value={tempSettings.thresholdPct} onChange={e => setTempSettings({...tempSettings, thresholdPct: e.target.value})} style={inputStyle} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: colors.textSub }}>Cooldown Chống Spam (Phút):</span>
                  <input type="number" value={tempSettings.cooldownMin} onChange={e => setTempSettings({...tempSettings, cooldownMin: e.target.value})} style={inputStyle} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: '13px', color: colors.textSub }}>Báo cáo định kỳ (Phút):</span>
                  <input type="number" value={tempSettings.heartbeatMin} onChange={e => setTempSettings({...tempSettings, heartbeatMin: e.target.value})} style={inputStyle} />
                </div>
              </div>
              
              <div style={{ fontSize: '12px', color: colors.warn, textAlign: 'center' }}>{status}</div>
              
              <div style={{ display: 'flex', gap: '8px', marginTop: '4px', flexWrap: 'wrap' }}>
                <button 
                  onClick={handleSaveSettings}
                  style={{ flex: 1, minWidth: '100px', backgroundColor: colors.greenBg, border: `1px solid ${colors.green}`, color: colors.green, padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  💾 LƯU
                </button>

                <button 
                  onClick={handleManualTestFull}
                  title="Gửi thử Báo cáo Full"
                  style={{ flex: 1, minWidth: '120px', backgroundColor: colors.bg, border: `1px solid ${colors.textSub}`, color: colors.textMain, padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
                >
                  📸 TEST FULL
                </button>

                <button 
                  onClick={handleManualTestMA}
                  title="Gửi thử Check MA"
                  style={{ flex: 1, minWidth: '100px', backgroundColor: colors.bg, border: `1px solid ${colors.textSub}`, color: colors.textMain, padding: '10px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '13px' }}
                >
                  📈 TEST MA
                </button>
              </div>

            </div>
          </div>
        </div>
      )}
    </>
  );
}
